'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

interface Client {
  id: string;
  name: string;
  type: string;
  industry: string;
  complianceScore: number;
  pendingTasks: number;
  urgentTasks: number;
  lastActivity: string;
  status: 'healthy' | 'attention' | 'critical';
  registrations: { gst: boolean; msme: boolean; dpiit: boolean; trademark: boolean };
  nextDeadline: string;
  nextDeadlineDays: number;
}

// Demo data — shown only when no real clients are loaded
const demoClients: Client[] = [
  {
    id: '1',
    name: 'Elysium Fragrances Pvt Ltd',
    type: 'Private Limited',
    industry: 'Consumer Goods',
    complianceScore: 94,
    pendingTasks: 2,
    urgentTasks: 1,
    lastActivity: '2 hours ago',
    status: 'healthy',
    registrations: { gst: true, msme: false, dpiit: true, trademark: true },
    nextDeadline: 'TDS Return Q3',
    nextDeadlineDays: 15,
  },
  {
    id: '2',
    name: 'NovaTech Solutions LLP',
    type: 'LLP',
    industry: 'Technology',
    complianceScore: 71,
    pendingTasks: 5,
    urgentTasks: 3,
    lastActivity: '1 day ago',
    status: 'attention',
    registrations: { gst: true, msme: true, dpiit: false, trademark: false },
    nextDeadline: 'Annual ROC Filing',
    nextDeadlineDays: 8,
  },
  {
    id: '3',
    name: 'GreenLeaf Organics OPC',
    type: 'OPC',
    industry: 'Agriculture',
    complianceScore: 58,
    pendingTasks: 7,
    urgentTasks: 4,
    lastActivity: '3 days ago',
    status: 'critical',
    registrations: { gst: false, msme: true, dpiit: false, trademark: false },
    nextDeadline: 'GST Registration',
    nextDeadlineDays: 3,
  },
];

const statusConfig = {
  healthy: { label: 'Healthy', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-600' },
  attention: { label: 'Needs Attention', color: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-600' },
  critical: { label: 'Critical', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-600' },
};

const entityTypeLabels: Record<string, string> = {
  private_limited: 'Private Limited',
  llp: 'LLP',
  opc: 'OPC',
  partnership: 'Partnership',
  sole_proprietorship: 'Sole Proprietorship',
};

export default function AdvisorDashboard() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'critical' | 'attention'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadClients() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        // Load businesses where this professional is authorized
        // Uses professional_assignments table (advisor can only see their assigned clients)
        const { data: assignments, error: assignErr } = await supabase
          .from('professional_assignments')
          .select(`
            business_id,
            role,
            businesses (
              id,
              legal_name,
              entity_type,
              setup_progress,
              compliance_profiles (health_score),
              compliance_events (id, status),
              trademark_applications (id, status),
              business_passports (msme_number, dpiit_number, gstin)
            )
          `)
          .eq('professional_id', user.id)
          .eq('is_active', true);

        if (assignErr && assignErr.code !== 'PGRST116') {
          // Table may not exist yet — fall through to demo
          console.warn('professional_assignments query failed:', assignErr.message);
        }

        if (assignments && assignments.length > 0) {
          const mapped: Client[] = assignments.map((a: any) => {
            const b = a.businesses;
            const complianceEvents = Array.isArray(b?.compliance_events) ? b.compliance_events : [];
            const urgentCount = complianceEvents.filter((e: any) => e.status === 'overdue').length;
            const pendingCount = complianceEvents.filter((e: any) => e.status === 'due_soon').length;
            const healthScore = Array.isArray(b?.compliance_profiles) ? (b.compliance_profiles[0]?.health_score || 0) : 0;
            const passport = Array.isArray(b?.business_passports) ? b.business_passports[0] : null;

            const status: 'healthy' | 'attention' | 'critical' =
              urgentCount > 0 ? 'critical' :
              pendingCount > 0 ? 'attention' : 'healthy';

            return {
              id: b?.id || a.business_id,
              name: b?.legal_name || 'Unknown Business',
              type: entityTypeLabels[b?.entity_type] || b?.entity_type || '—',
              industry: '—',
              complianceScore: healthScore,
              pendingTasks: pendingCount,
              urgentTasks: urgentCount,
              lastActivity: 'Recently',
              status,
              registrations: {
                gst: !!passport?.gstin,
                msme: !!passport?.msme_number,
                dpiit: !!passport?.dpiit_number,
                trademark: Array.isArray(b?.trademark_applications) && b.trademark_applications.length > 0,
              },
              nextDeadline: '—',
              nextDeadlineDays: 0,
            };
          });
          setClients(mapped);
        } else {
          // No real clients — show demo data with indicator
          setClients(demoClients);
          setIsDemo(true);
        }
      } catch (err) {
        console.error('Advisor load error:', err);
        setError('Failed to load client data. Please refresh.');
        setClients(demoClients);
        setIsDemo(true);
      } finally {
        setLoading(false);
      }
    }
    loadClients();
  }, []);

  const filteredClients = clients.filter(c => {
    const matchesTab = activeTab === 'all' || c.status === activeTab;
    const matchesSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const totalUrgent = clients.reduce((sum, c) => sum + c.urgentTasks, 0);
  const totalPending = clients.reduce((sum, c) => sum + c.pendingTasks, 0);
  const criticalCount = clients.filter(c => c.status === 'critical').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/advisor" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-surface-3 rounded-xl w-48" />
              <div className="grid grid-cols-3 gap-4">
                {[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-border" />)}
              </div>
              {[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-border" />)}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/advisor" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-ink mb-1">Professional Portal</h1>
              <p className="text-[14px] text-ink-muted">Manage your client businesses and compliance tasks.</p>
            </div>
            {isDemo && (
              <span className="self-start sm:self-auto text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-full">
                DEMO DATA — No real clients assigned yet
              </span>
            )}
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" role="alert">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-[13px] font-semibold text-red-800">{error}</p>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
            <div className="bg-white rounded-xl border border-border p-4 sm:p-5">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">Total Clients</p>
              <p className="text-3xl font-bold text-ink">{clients.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-border p-4 sm:p-5">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">Critical</p>
              <p className={`text-3xl font-bold ${criticalCount > 0 ? 'text-red-700' : 'text-ink'}`}>{criticalCount}</p>
            </div>
            <div className="bg-white rounded-xl border border-border p-4 sm:p-5 col-span-2 sm:col-span-1">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">Urgent Tasks</p>
              <p className={`text-3xl font-bold ${totalUrgent > 0 ? 'text-amber-800' : 'text-ink'}`}>{totalUrgent + totalPending}</p>
            </div>
          </div>

          {/* Search + Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="search"
                placeholder="Search clients…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                aria-label="Search clients"
                className="w-full pl-9 pr-4 py-2.5 bg-white border border-border rounded-xl text-[13px] text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai focus:border-transparent"
              />
            </div>
            <div className="flex gap-2" role="group" aria-label="Filter clients by status">
              {(['all', 'critical', 'attention'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  aria-pressed={activeTab === tab}
                  className={`px-3 py-2 rounded-xl text-[12px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                    activeTab === tab ? 'bg-ai text-white' : 'bg-white border border-border text-ink-muted hover:text-ink'
                  }`}
                >
                  {tab === 'all' ? 'All' : tab === 'critical' ? '🔴 Critical' : '🟡 Attention'}
                </button>
              ))}
            </div>
          </div>

          {/* Empty state */}
          {filteredClients.length === 0 && (
            <div className="bg-white rounded-2xl border border-border p-10 text-center">
              <div className="w-12 h-12 bg-surface-3 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-[13px] font-semibold text-ink mb-1">No clients found</p>
              <p className="text-[12px] text-ink-muted">
                {searchQuery ? 'Try a different search term.' : 'No clients match this filter.'}
              </p>
            </div>
          )}

          {/* Client Cards */}
          <div className="space-y-4" role="list" aria-label="Client list">
            {filteredClients.map(client => {
              const config = statusConfig[client.status];
              return (
                <div
                  key={client.id}
                  role="listitem"
                  className={`bg-white rounded-2xl border p-5 sm:p-6 transition-all hover:shadow-sm ${config.border}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-bold text-ink text-[15px] truncate">{client.name}</h3>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${config.bg} ${config.color} flex-shrink-0`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${config.dot} mr-1`} aria-hidden="true" />
                          {config.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[12px] text-ink-muted flex-wrap">
                        <span>{client.type}</span>
                        <span aria-hidden="true">·</span>
                        <span>{client.industry}</span>
                        <span aria-hidden="true">·</span>
                        <span>Last active: {client.lastActivity}</span>
                      </div>

                      {/* Registrations */}
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {Object.entries(client.registrations).map(([key, val]) => (
                          <span
                            key={key}
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              val ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
                            }`}
                            aria-label={`${key.toUpperCase()}: ${val ? 'registered' : 'not registered'}`}
                          >
                            {key.toUpperCase()} {val ? '✓' : '—'}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex sm:flex-col items-center sm:items-end gap-4 sm:gap-2 flex-shrink-0">
                      <div className="text-center sm:text-right">
                        <p className="text-[11px] text-ink-subtle uppercase tracking-wide">Compliance</p>
                        <p className={`text-2xl font-bold ${
                          client.complianceScore >= 80 ? 'text-green-700' :
                          client.complianceScore >= 50 ? 'text-amber-800' : 'text-red-700'
                        }`}>{client.complianceScore > 0 ? `${client.complianceScore}%` : '—'}</p>
                      </div>
                      {(client.urgentTasks > 0 || client.pendingTasks > 0) && (
                        <div className="flex gap-2">
                          {client.urgentTasks > 0 && (
                            <span className="text-[11px] font-bold bg-red-50 text-red-700 px-2 py-0.5 rounded-full">
                              {client.urgentTasks} urgent
                            </span>
                          )}
                          {client.pendingTasks > 0 && (
                            <span className="text-[11px] font-bold bg-amber-50 text-amber-800 px-2 py-0.5 rounded-full">
                              {client.pendingTasks} pending
                            </span>
                          )}
                        </div>
                      )}
                      <Link
                        href={`/advisor/tasks?client=${client.id}`}
                        className="text-[12px] font-semibold text-ai hover:text-ai-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai rounded"
                        aria-label={`View tasks for ${client.name}`}
                      >
                        View tasks →
                      </Link>
                    </div>
                  </div>

                  {/* Next deadline */}
                  {client.nextDeadline !== '—' && client.nextDeadlineDays <= 30 && (
                    <div className={`mt-4 pt-4 border-t border-border flex items-center gap-2 text-[12px] ${
                      client.nextDeadlineDays <= 7 ? 'text-red-700' : 'text-amber-800'
                    }`}>
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        <strong>{client.nextDeadline}</strong> due in {client.nextDeadlineDays} days
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
